import { useSitemapImport } from '@components/App/Modal/Import/hooks/useSitemapImport'
import { openModal, openSystemDialog } from '@storeOld/reducers/ui'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { SELECT_PAGES_IF_MORE } from '@/constants/env'
import {
  type ImportSitemapData,
  ImportSitemapResponseTypeSitemapIndex,
  ImportSitemapResponseTypeUrlset,
  ProjectOrigin,
} from '@/types'
import { CANCEL_CRAWLING } from '../../../SystemDialogs/constants'
import {
  IMPORT_PROJECT_MULTIPLE_SITEMAPS,
  IMPORT_SELECT_URLS,
} from '../../constants'
import useCommit from './useCommit'
import { useCommitIntoProject } from './useCommitIntoProject'

type UseSitemapProps = {
  metaTag: boolean
  teamId?: string
  folderId?: string | null
  // Land the pages in the project the user already has open, as a new section.
  intoOpenProject?: boolean
}

const useSitemap = ({
  teamId,
  folderId,
  metaTag,
  intoOpenProject = false,
}: UseSitemapProps) => {
  const dispatch = useDispatch()
  const [error, setError] = useState('')

  const { importUrls } = useSitemapImport()

  const commitNewProject = useCommit({
    teamId,
    folderId,
    projectOrigin: ProjectOrigin.SITEMAP_XML,
    popupDownloadSitemapXml: false,
  })

  const commitIntoProject = useCommitIntoProject()

  const handleCommit = intoOpenProject ? commitIntoProject : commitNewProject

  // Importing into a project that already has pages always shows what is about to be
  // added: the small-import shortcut only makes sense when the import IS the project.
  const alwaysConfirm = intoOpenProject

  const read = (payload: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => {
        reader.abort()
        reject()
      }
      reader.readAsText(payload)
    })
  }

  const parse = (payload: any) => {
    try {
      const doc = new DOMParser().parseFromString(payload, 'text/xml')
      const type = doc.documentElement.tagName
      const result = {
        type: doc.documentElement.tagName,
        data: Array.from(doc.getElementsByTagName('loc')).map(
          (cur: any) => cur.firstChild.nodeValue
        ),
      }

      if (type === ImportSitemapResponseTypeSitemapIndex) {
        return result
      }

      return {
        ...result,
        data: result.data.map((url) => ({
          url,
          title: null,
          description: null,
        })),
        extra: null,
      }
    } catch (err) {
      console.error(err)
      return { type: ImportSitemapResponseTypeUrlset, data: [] }
    }
  }

  const confirmClosing = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        openSystemDialog(CANCEL_CRAWLING, {
          callback: resolve,
        })
      )
    })

  const openSelectPages = (payload: ImportSitemapData) => {
    dispatch(
      // @ts-expect-error
      openModal(IMPORT_SELECT_URLS, {
        data: payload,
        handleCommit,
        confirmClosing,
        limitExceeded: payload.limitExceeded,
      })
    )
  }

  /**
   * What the multi-sitemap screen calls once it has fetched the files the user picked.
   *
   * It cannot be `handleCommit` itself: landing in an open project goes through the
   * page-selection screen like every other path into a project, and this is the one place
   * that used to skip it — hundreds of pages went straight in with nothing to confirm and
   * no way back.
   */
  const commitFromMultipleSitemaps = (
    payload: ImportSitemapData
  ): Promise<void> => {
    if (alwaysConfirm) {
      openSelectPages(payload)

      return Promise.resolve()
    }

    return handleCommit(payload)
  }

  const processCompleteResult = (result: ImportSitemapData[], url: string) => {
    const payload = result[0]
    if (payload.type === ImportSitemapResponseTypeUrlset) {
      if (alwaysConfirm || payload.data.length > SELECT_PAGES_IF_MORE) {
        openSelectPages(payload)
      } else {
        handleCommit(payload)
      }
    } else {
      dispatch(
        // @ts-expect-error
        openModal(IMPORT_PROJECT_MULTIPLE_SITEMAPS, {
          url,
          urlsList: payload.data,
          meta: metaTag,
          intoOpenProject,
          handleCommit: commitFromMultipleSitemaps,
        })
      )
    }
  }

  const runFromUrls = async ({ urls }: { urls: string[] }) => {
    importUrls({
      urls,
      options: { meta: metaTag },
      onComplete: processCompleteResult,
      onError: setError,
    })
  }

  const runFromFile = async ({ file }: { file: File }) => {
    await read(file)
      .then((result) => parse(result))
      .then((result: any) => {
        if (result.data.length === 0) {
          throw new Error()
        } else if (result.type === ImportSitemapResponseTypeSitemapIndex) {
          dispatch(
            // @ts-expect-error
            openModal(IMPORT_PROJECT_MULTIPLE_SITEMAPS, {
              url: null,
              urlsList: result.data,
              meta: metaTag,
              intoOpenProject,
              handleCommit: commitFromMultipleSitemaps,
            })
          )
        } else if (
          alwaysConfirm ||
          result?.data?.length > SELECT_PAGES_IF_MORE
        ) {
          openSelectPages(result)
        } else {
          handleCommit(result)
        }
      })
      .catch(() => {
        setError('Sitemap.xml not found')
      })
  }

  return {
    error,
    setError,
    runFromUrls,
    runFromFile,
  }
}

export default useSitemap
